import React, { useState, useMemo } from 'react';
import { ArrowLeft, CreditCard, Plus, Trash2, Star, Check, Smartphone, Building2, X } from 'lucide-react';
import { useI18n } from '../i18n';
import { useApp } from '../context/AppContext';
import { getPaymentMethodsByUserId } from '../data/database';

const PaymentMethodsPage = ({ onBack, onNavigate }) => {
  const { t } = useI18n();
  const { user } = useApp();
  
  // Carica metodi di pagamento dal database
  const initialMethods = useMemo(() => {
    if (!user?.id) return [];
    return getPaymentMethodsByUserId(user.id).map(m => ({
      ...m,
      icon: m.type === 'card' ? '💳' : '🅿️'
    }));
  }, [user?.id]);
  
  const [methods, setMethods] = useState(initialMethods);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCard, setNewCard] = useState({
    number: '',
    expiry: '',
    cvv: '',
    name: ''
  });

  const setAsDefault = (id) => {
    setMethods(prev => prev.map(m => ({ ...m, isDefault: m.id === id })));
  };

  const deleteMethod = (id) => {
    setMethods(prev => prev.filter(m => m.id !== id));
  };

  const handleAddCard = () => {
    if (newCard.number && newCard.expiry && newCard.cvv && newCard.name) {
      const newMethod = {
        id: Date.now(),
        type: 'card',
        brand: newCard.number.startsWith('4') ? 'Visa' : 'Mastercard',
        last4: newCard.number.slice(-4),
        expiry: newCard.expiry,
        isDefault: methods.length === 0,
        icon: '💳'
      };
      setMethods(prev => [...prev, newMethod]);
      setNewCard({ number: '', expiry: '', cvv: '', name: '' });
      setShowAddModal(false);
    }
  };

  const getBrandLogo = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return (
          <div className="w-10 h-6 bg-blue-600 rounded flex items-center justify-center text-white text-xs font-bold">
            VISA
          </div>
        );
      case 'mastercard':
        return (
          <div className="w-10 h-6 bg-gradient-to-r from-red-500 to-yellow-500 rounded flex items-center justify-center">
            <div className="w-3 h-3 bg-red-600 rounded-full -mr-1"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
          </div>
        );
      default:
        return <CreditCard className="w-6 h-6 text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full" data-testid="payments-back-btn">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">{t('payments', 'paymentMethods')}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 py-4">
        {/* Saved Methods */}
        <div className="mb-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3">{t('payments', 'yourMethods')}</h2>
          
          {methods.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center" data-testid="no-payment-methods">
              <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t('payments', 'noMethods')}</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="payment-methods-list">
              {methods.map((method) => (
                <div 
                  key={method.id} 
                  className={`bg-white rounded-2xl p-4 shadow-sm ${method.isDefault ? 'ring-2 ring-emerald-500' : ''}`}
                  data-testid={`payment-method-${method.id}`}
                >
                  <div className="flex items-center gap-4">
                    {method.type === 'card' ? (
                      getBrandLogo(method.brand)
                    ) : (
                      <div className="w-10 h-6 bg-blue-600 rounded flex items-center justify-center text-white text-xs font-bold">
                        PP
                      </div>
                    )}
                    
                    <div className="flex-1">
                      {method.type === 'card' ? (
                        <>
                          <p className="font-medium text-gray-900">
                            {method.brand} •••• {method.last4}
                          </p>
                          <p className="text-sm text-gray-500">{t('payments', 'expires')} {method.expiry}</p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium text-gray-900">PayPal</p>
                          <p className="text-sm text-gray-500">{method.email}</p>
                        </>
                      )}
                    </div>

                    {method.isDefault && (
                      <span className="bg-emerald-100 text-emerald-700 text-xs font-medium px-2 py-1 rounded-full">
                        {t('payments', 'default')}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                    {!method.isDefault && (
                      <button 
                        onClick={() => setAsDefault(method.id)}
                        className="flex-1 text-sm text-emerald-600 font-medium py-2 hover:bg-emerald-50 rounded-lg transition-colors"
                        data-testid={`set-default-${method.id}`}
                      >
                        {t('payments', 'setAsDefault')}
                      </button>
                    )}
                    <button 
                      onClick={() => deleteMethod(method.id)}
                      className={`${method.isDefault ? 'flex-1' : ''} text-sm text-red-500 font-medium py-2 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-1`}
                      data-testid={`delete-method-${method.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('payments', 'remove')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add New Method */}
        <div>
          <h2 className="text-sm font-medium text-gray-500 mb-3">{t('payments', 'addNew')}</h2>
          <div className="space-y-3">
            <button 
              onClick={() => setShowAddModal(true)}
              className="w-full bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4 hover:bg-gray-50 transition-colors"
              data-testid="add-card-btn"
            >
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-gray-900">{t('payments', 'creditDebitCard')}</p>
                <p className="text-sm text-gray-500">{t('payments', 'visaMastercardAmex')}</p>
              </div>
              <Plus className="w-5 h-5 text-gray-400" />
            </button>

            <button 
              className="w-full bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4 hover:bg-gray-50 transition-colors"
              data-testid="add-paypal-btn"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <div className="text-blue-600 font-bold text-lg">PP</div>
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-gray-900">PayPal</p>
                <p className="text-sm text-gray-500">{t('payments', 'linkPaypal')}</p>
              </div>
              <Plus className="w-5 h-5 text-gray-400" />
            </button>

            <button 
              className="w-full bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4 hover:bg-gray-50 transition-colors"
              data-testid="add-apple-pay-btn"
            >
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-gray-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-gray-900">Apple Pay / Google Pay</p>
                <p className="text-sm text-gray-500">{t('payments', 'quickPayment')}</p>
              </div>
              <Plus className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Security Note */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-blue-900 text-sm">{t('payments', 'securePayments')}</p>
              <p className="text-xs text-blue-700 mt-1">
                {t('payments', 'securityNote')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Card Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5 animate-slide-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">{t('payments', 'addCard')}</h3>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('payments', 'cardNumber')}</label>
                <input
                  type="text"
                  value={newCard.number}
                  onChange={(e) => setNewCard({...newCard, number: e.target.value.replace(/\D/g, '').slice(0, 16)})}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500"
                  placeholder="1234 5678 9012 3456"
                  maxLength={16}
                  data-testid="card-number-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('payments', 'expiry')}</label>
                  <input
                    type="text"
                    value={newCard.expiry}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length >= 2) {
                        value = value.slice(0, 2) + '/' + value.slice(2, 4);
                      }
                      setNewCard({...newCard, expiry: value});
                    }}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500"
                    placeholder="MM/AA"
                    maxLength={5}
                    data-testid="card-expiry-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">CVV</label>
                  <input
                    type="text"
                    value={newCard.cvv}
                    onChange={(e) => setNewCard({...newCard, cvv: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500"
                    placeholder="123"
                    maxLength={4}
                    data-testid="card-cvv-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('payments', 'nameOnCard')}</label>
                <input
                  type="text"
                  value={newCard.name}
                  onChange={(e) => setNewCard({...newCard, name: e.target.value.toUpperCase()})}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500"
                  placeholder="MARIO ROSSI"
                  data-testid="card-name-input"
                />
              </div>
            </div>

            <button 
              onClick={handleAddCard}
              disabled={!newCard.number || !newCard.expiry || !newCard.cvv || !newCard.name}
              className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold mt-6 hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-card-btn"
            >
              {t('payments', 'saveCard')}
            </button>

            <p className="text-center text-xs text-gray-500 mt-4">
              {t('payments', 'secureSSL')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodsPage;
